import Hotkeys from 'react-hot-keys';
import { motion } from 'framer-motion';
import { DocProfileResponse } from '../../../api/fetchDoctorProfile';
import { NO_FOOTER, NO_HEADER } from '../EditTemplate';
import ModalDialog from '../../../components/common/ModalDialog';

const CopyRxTemplateModal: React.FC<{
    onCancel: () => void;
    onCopy: () => void;
    docProfile: DocProfileResponse;
    activeClinic: string;
    isCopyLoading: boolean;
}> = ({ onCancel, onCopy, docProfile, activeClinic, isCopyLoading }) => {
    const clinics = docProfile?.profile?.professional?.clinics;
    const activeClinicName = clinics?.find((clinic) => clinic._id === activeClinic)?.name;

    const doctorTemplateData = docProfile.profile.professional.templates_v2;
    const rxConfig = doctorTemplateData?.find(
        (data) => data.clinicId === activeClinic && data.type === 'PRINT',
    );

    return (
        <ModalDialog>
            <Hotkeys keyName="esc">
                <motion.div
                    initial={{ opacity: 0 }}
                    exit={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{ zIndex: 20 }}
                    transition={{ duration: 0.2, ease: 'linear' }}
                    className="font-lato select-none"
                >
                    <div className="absolute inset-0 bg-darwin-neutral-1000 bg-opacity-50 z-50"></div>
                    <div
                        className="modal-content space-y-16"
                        style={{
                            position: 'absolute',
                            width: '500px',
                            background: 'transparent',
                        }}
                    >
                        <div className="p-24 bg-white flex flex-col rounded-12 items-center space-y-12">
                            <div className="flex flex-col items-center space-y-8">
                                <svg
                                    className="w-36 h-36 fill-darwin-status-warning-500"
                                    viewBox="0 0 18 14"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M9 0C9.4375 0 9.84375 0.25 10.0625 0.625L16.8125 12.125C17.0312 12.5312 17.0312 13 16.8125 13.375C16.5938 13.7812 16.1875 14 15.75 14H2.25C1.78125 14 1.375 13.7812 1.15625 13.375C0.9375 13 0.9375 12.5312 1.15625 12.125L7.90625 0.625C8.125 0.25 8.53125 0 9 0ZM9 4C8.5625 4 8.25 4.34375 8.25 4.75V8.25C8.25 8.6875 8.5625 9 9 9C9.40625 9 9.75 8.6875 9.75 8.25V4.75C9.75 4.34375 9.40625 4 9 4ZM10 11C10 10.4688 9.53125 10 9 10C8.4375 10 8 10.4688 8 11C8 11.5625 8.4375 12 9 12C9.53125 12 10 11.5625 10 11Z"></path>
                                </svg>
                                <span className="text-20 text-center">
                                    Are you sure you want to copy Print Rx <br></br> template of{' '}
                                    <span className="font-700">{activeClinicName}</span> to its
                                    Digital Rx template?
                                </span>
                            </div>

                            <span className="text-14 px-16 space-y-8 text-darwin-neutral-800 text-center pt-8">
                                <span>Please note</span>

                                <div className="space-y-8 max-h-200 overflow-y-auto px-4">
                                    {rxConfig?.header_img &&
                                    rxConfig.header_img !== NO_HEADER ? null : (
                                        <div className="border border-darwin-accent-investigations-ochre-800 bg-darwin-accent-investigations-ochre-100 px-8 py-10 space-x-6 rounded-8 flex">
                                            <span className="pt-3 pl-2">
                                                <svg
                                                    className="w-16 h-16 fill-darwin-accent-investigations-ochre-800"
                                                    viewBox="0 0 14 15"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path d="M7 14.75C10.8555 14.75 14 11.6328 14 7.75C14 3.89453 10.8555 0.75 7 0.75C3.11719 0.75 0 3.89453 0 7.75C0 11.6328 3.11719 14.75 7 14.75ZM7 4.25C7.35547 4.25 7.65625 4.55078 7.65625 4.90625V7.96875C7.65625 8.35156 7.35547 8.625 7 8.625C6.61719 8.625 6.34375 8.35156 6.34375 7.96875V4.90625C6.34375 4.55078 6.61719 4.25 7 4.25ZM7.875 10.375C7.875 10.8672 7.46484 11.25 7 11.25C6.50781 11.25 6.125 10.8672 6.125 10.375C6.125 9.91016 6.50781 9.5 7 9.5C7.46484 9.5 7.875 9.91016 7.875 10.375Z"></path>
                                                </svg>
                                            </span>
                                            <span className="text-13 text-left">
                                                <span className="font-700">Header</span> is
                                                mandatory. If no header is added to the Print Rx
                                                template, default Eka header will be added to the
                                                Digital Rx template.
                                            </span>
                                        </div>
                                    )}

                                    {rxConfig?.floating_header === true && (
                                        <div className="border border-darwin-accent-investigations-ochre-800 bg-darwin-accent-investigations-ochre-100 px-8 py-10 space-x-6 rounded-8 flex">
                                            <span className="pt-3 pl-2">
                                                <svg
                                                    className="w-16 h-16 fill-darwin-accent-investigations-ochre-800"
                                                    viewBox="0 0 14 15"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path d="M7 14.75C10.8555 14.75 14 11.6328 14 7.75C14 3.89453 10.8555 0.75 7 0.75C3.11719 0.75 0 3.89453 0 7.75C0 11.6328 3.11719 14.75 7 14.75ZM7 4.25C7.35547 4.25 7.65625 4.55078 7.65625 4.90625V7.96875C7.65625 8.35156 7.35547 8.625 7 8.625C6.61719 8.625 6.34375 8.35156 6.34375 7.96875V4.90625C6.34375 4.55078 6.61719 4.25 7 4.25ZM7.875 10.375C7.875 10.8672 7.46484 11.25 7 11.25C6.50781 11.25 6.125 10.8672 6.125 10.375C6.125 9.91016 6.50781 9.5 7 9.5C7.46484 9.5 7.875 9.91016 7.875 10.375Z"></path>
                                                </svg>
                                            </span>
                                            <span className="text-13 text-left">
                                                <span className="font-700">Floating header</span>{' '}
                                                option doesn't exist in Digital Rx will be replaced
                                                with normal header
                                            </span>
                                        </div>
                                    )}

                                    {rxConfig?.footer_img &&
                                    rxConfig.footer_img !== NO_FOOTER ? null : (
                                        <div className="border border-darwin-accent-investigations-ochre-800 bg-darwin-accent-investigations-ochre-100 px-8 py-10 space-x-6 rounded-8 flex">
                                            <span className="pt-3 pl-2">
                                                <svg
                                                    className="w-16 h-16 fill-darwin-accent-investigations-ochre-800"
                                                    viewBox="0 0 14 15"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path d="M7 14.75C10.8555 14.75 14 11.6328 14 7.75C14 3.89453 10.8555 0.75 7 0.75C3.11719 0.75 0 3.89453 0 7.75C0 11.6328 3.11719 14.75 7 14.75ZM7 4.25C7.35547 4.25 7.65625 4.55078 7.65625 4.90625V7.96875C7.65625 8.35156 7.35547 8.625 7 8.625C6.61719 8.625 6.34375 8.35156 6.34375 7.96875V4.90625C6.34375 4.55078 6.61719 4.25 7 4.25ZM7.875 10.375C7.875 10.8672 7.46484 11.25 7 11.25C6.50781 11.25 6.125 10.8672 6.125 10.375C6.125 9.91016 6.50781 9.5 7 9.5C7.46484 9.5 7.875 9.91016 7.875 10.375Z"></path>
                                                </svg>
                                            </span>
                                            <span className="text-13 text-left">
                                                <span className="font-700">Footer</span> is
                                                mandatory. If no footer is added to the Print Rx
                                                template, default Eka footer will be added to the
                                                Digital Rx template.
                                            </span>
                                        </div>
                                    )}

                                    {rxConfig?.floating_footer === true && (
                                        <div className="border border-darwin-accent-investigations-ochre-800 bg-darwin-accent-investigations-ochre-100 px-8 py-10 space-x-6 rounded-8 flex">
                                            <span className="pt-3 pl-2">
                                                <svg
                                                    className="w-16 h-16 fill-darwin-accent-investigations-ochre-800"
                                                    viewBox="0 0 14 15"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path d="M7 14.75C10.8555 14.75 14 11.6328 14 7.75C14 3.89453 10.8555 0.75 7 0.75C3.11719 0.75 0 3.89453 0 7.75C0 11.6328 3.11719 14.75 7 14.75ZM7 4.25C7.35547 4.25 7.65625 4.55078 7.65625 4.90625V7.96875C7.65625 8.35156 7.35547 8.625 7 8.625C6.61719 8.625 6.34375 8.35156 6.34375 7.96875V4.90625C6.34375 4.55078 6.61719 4.25 7 4.25ZM7.875 10.375C7.875 10.8672 7.46484 11.25 7 11.25C6.50781 11.25 6.125 10.8672 6.125 10.375C6.125 9.91016 6.50781 9.5 7 9.5C7.46484 9.5 7.875 9.91016 7.875 10.375Z"></path>
                                                </svg>
                                            </span>
                                            <span className="text-13 text-left">
                                                <span className="font-700">Floating footer</span>{' '}
                                                option doesnot exist in Digital Rx will be replaced
                                                with normal header
                                            </span>
                                        </div>
                                    )}

                                    {(rxConfig?.margin_left &&
                                        rxConfig?.margin_left !== '1.27cm') ||
                                    (rxConfig?.margin_right &&
                                        rxConfig?.margin_right !== '1.27cm') ? (
                                        <div className="border border-darwin-accent-investigations-ochre-800 bg-darwin-accent-investigations-ochre-100 px-8 py-10 space-x-6 rounded-8 flex">
                                            <span className="pt-3 pl-2">
                                                <svg
                                                    className="w-16 h-16 fill-darwin-accent-investigations-ochre-800"
                                                    viewBox="0 0 14 15"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path d="M7 14.75C10.8555 14.75 14 11.6328 14 7.75C14 3.89453 10.8555 0.75 7 0.75C3.11719 0.75 0 3.89453 0 7.75C0 11.6328 3.11719 14.75 7 14.75ZM7 4.25C7.35547 4.25 7.65625 4.55078 7.65625 4.90625V7.96875C7.65625 8.35156 7.35547 8.625 7 8.625C6.61719 8.625 6.34375 8.35156 6.34375 7.96875V4.90625C6.34375 4.55078 6.61719 4.25 7 4.25ZM7.875 10.375C7.875 10.8672 7.46484 11.25 7 11.25C6.50781 11.25 6.125 10.8672 6.125 10.375C6.125 9.91016 6.50781 9.5 7 9.5C7.46484 9.5 7.875 9.91016 7.875 10.375Z"></path>
                                                </svg>
                                            </span>
                                            <span className="text-13 text-left">
                                                Your{' '}
                                                <span className="font-700">
                                                    Left & Right Page Spacing
                                                </span>{' '}
                                                will be replaced with the default spacing.
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                            </span>

                            <div className="flex items-center space-x-16 pt-24">
                                <button
                                    onClick={onCancel}
                                    className="flex px-16 py-6 secondary-btn btn--small cursor-pointer items-center"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={onCopy}
                                    className="flex space-x-4 px-12 py-6 bg-white border rounded-4 text-darwin-status-warning-500 btn--small cursor-pointer items-center"
                                >
                                    <span>{isCopyLoading ? 'Copying...' : 'Yes, Copy'}</span>
                                    {isCopyLoading && (
                                        <div className="w-20 h-20">
                                            <svg className="circular-loader" viewBox="25 25 50 50">
                                                <circle
                                                    className="loader-path"
                                                    cx="50"
                                                    cy="50"
                                                    r="20"
                                                    fill="none"
                                                    stroke="#000000"
                                                    stroke-width="3"
                                                ></circle>
                                            </svg>
                                        </div>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </Hotkeys>
        </ModalDialog>
    );
};

export default CopyRxTemplateModal;
